import { useState, useRef, useEffect } from 'react';
import { ChevronDown, Search, X, ChevronLeft, ChevronRight } from 'lucide-react';
import { motion, AnimatePresence } from 'framer-motion';
import { useDropdownData } from '../hooks/useDropdownData';
import { usePanelPosition } from '../hooks/usePanelPosition';
import SelectedItemBubbles from '../SelectedItemBubbles';

export interface UnifiedFilterDropdownProps<T> {
  title: string;
  icon: React.ReactNode;
  selectedIds: number[];
  onSelectionChange: (ids: number[]) => void;
  endpoint: string;
  getId: (item: T) => number;
  getDisplayName: (item: T) => string;
  getSubtext?: (item: T) => string;
  variant?: 'simple' | 'advanced';
  showSelectedBubbles?: boolean;
  itemsPerPage?: number;
  /** 'viewport' keeps the panel on screen when the dropdown sits inside a modal. */
  panelPlacement?: 'responsive' | 'viewport';
}

export default function UnifiedFilterDropdown<T>({
  title,
  icon,
  selectedIds,
  onSelectionChange,
  endpoint,
  getId,
  getDisplayName,
  getSubtext,
  variant = 'simple',
  showSelectedBubbles = variant === 'advanced',
  itemsPerPage = 8,
  panelPlacement = 'responsive',
}: UnifiedFilterDropdownProps<T>) {
  const [isOpen, setIsOpen] = useState(false);
  const [searchQuery, setSearchQuery] = useState('');
  const [currentPage, setCurrentPage] = useState(1);

  const wrapperRef = useRef<HTMLDivElement>(null);
  const searchInputRef = useRef<HTMLInputElement>(null);
  const panelPosition = usePanelPosition(wrapperRef, isOpen, {
    maxWidth: 400,
    panelHeight: panelPlacement === 'viewport' ? 400 : 300,
    desktopMinWidth: 280,
    strategy: panelPlacement,
  });

  const { items, totalPages, isLoading, getItemName } = useDropdownData(
    { endpoint, getId, getDisplayName, getSubtext },
    { searchQuery, currentPage, itemsPerPage },
  );

  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      if (wrapperRef.current && !wrapperRef.current.contains(event.target as Node)) {
        setIsOpen(false);
      }
    };
    if (isOpen) {
      document.addEventListener('mousedown', handleClickOutside);
      return () => document.removeEventListener('mousedown', handleClickOutside);
    }
  }, [isOpen]);

  useEffect(() => {
    if (isOpen && searchInputRef.current) {
      searchInputRef.current.focus();
    }
    if (!isOpen) {
      setSearchQuery('');
      setCurrentPage(1);
    }
  }, [isOpen]);

  const handleToggleItem = (id: number) => {
    const newIds = selectedIds.includes(id)
      ? selectedIds.filter(existingId => existingId !== id)
      : [...selectedIds, id];
    onSelectionChange(newIds);
  };

  const handleRemoveItem = (id: number) => {
    onSelectionChange(selectedIds.filter(existingId => existingId !== id));
  };

  const handleSearchClear = () => {
    setSearchQuery('');
    setCurrentPage(1);
  };

  const hasSelected = selectedIds.length > 0;
  const canPrevPage = currentPage > 1;
  const canNextPage = currentPage < totalPages;

  const buttonStyles = hasSelected
    ? 'text-white'
    : 'text-slate-200 hover:bg-[var(--anl-surface-elev)] border-white/20 bg-[#454b4d]';

  // Build panel className and style from position
  const panelPositionClass =
    panelPosition.type === 'absolute'
      ? panelPosition.anchor === 'right'
        ? 'absolute right-0 top-full mt-1 min-w-[280px]'
        : 'absolute left-0 top-full mt-1 min-w-[280px]'
      : 'fixed';

  const panelStyle =
    panelPosition.type === 'fixed'
      ? {
          backgroundColor: '#454b4d' as const,
          left: panelPosition.left,
          top: panelPosition.top,
          bottom: panelPosition.bottom,
          width: panelPosition.width,
        }
      : { backgroundColor: '#454b4d' as const };

  return (
    <div className="relative" ref={wrapperRef}>
      <button
        onClick={() => setIsOpen(!isOpen)}
        className={`flex items-center space-x-2 rounded-lg border px-3 py-2 text-sm font-medium transition-all duration-200 focus:outline-none ${buttonStyles}`}
        style={
          hasSelected
            ? { backgroundColor: 'var(--anl-warning)', borderColor: 'var(--anl-warning)' }
            : undefined
        }
      >
        {icon}
        <span>
          {title}
          {hasSelected && (
            <span
              className={`ml-1 text-xs ${variant === 'advanced' ? 'font-normal opacity-75' : ''}`}
            >
              {variant === 'simple' ? ` (${selectedIds.length})` : `(${selectedIds.length})`}
            </span>
          )}
        </span>
        <ChevronDown className={`h-4 w-4 transition-transform ${isOpen ? 'rotate-180' : ''}`} />
      </button>

      <AnimatePresence>
        {isOpen && (
          <motion.div
            initial={{ scale: 0.95, opacity: 0, y: -8 }}
            animate={{ scale: 1, opacity: 1, y: 0 }}
            exit={{ scale: 0.95, opacity: 0, y: -8 }}
            transition={{ duration: 0.15, ease: 'easeOut' }}
            className={`z-50 overflow-hidden rounded-lg border border-[var(--anl-border)] shadow-xl ${panelPositionClass}`}
            style={panelStyle}
          >
            {showSelectedBubbles && hasSelected && (
              <div className="border-b border-[var(--anl-border)] p-3">
                <div className="mb-2">
                  <span className="text-xs font-medium text-slate-300">Selected {title}:</span>
                </div>
                <SelectedItemBubbles
                  selectedIds={selectedIds}
                  onRemoveItem={handleRemoveItem}
                  getItemName={getItemName}
                />
              </div>
            )}

            <div className="border-b border-[var(--anl-border)] p-3">
              <div
                className="flex items-center gap-2 rounded-md border border-[var(--anl-border)] px-3 py-2"
                style={{ backgroundColor: '#353a3c' }}
              >
                <Search size={16} className="flex-shrink-0 text-slate-400" />
                <input
                  ref={searchInputRef}
                  type="text"
                  className="flex-1 border-none bg-transparent text-sm text-slate-100 placeholder-slate-400 outline-none"
                  placeholder={`Search ${title.toLowerCase()}...`}
                  value={searchQuery}
                  onChange={e => setSearchQuery(e.target.value)}
                />
                {searchQuery && (
                  <button
                    className="rounded p-1 text-slate-400 transition-colors hover:bg-[var(--anl-surface)] hover:text-slate-200"
                    onClick={handleSearchClear}
                  >
                    <X size={14} />
                  </button>
                )}
              </div>
            </div>

            <div className="max-h-56 overflow-y-auto">
              {isLoading ? (
                <div className="p-4 text-center text-[var(--anl-text-muted)]">
                  <div className="mx-auto mb-2 h-6 w-6 rounded-full border-2 border-[var(--anl-text-muted)] border-t-transparent motion-safe:animate-spin" />
                  Loading...
                </div>
              ) : items.length === 0 ? (
                <div className="p-4 text-center text-slate-400">
                  {searchQuery
                    ? `No ${title.toLowerCase()} found matching "${searchQuery}"`
                    : `No ${title.toLowerCase()} found`}
                </div>
              ) : (
                <div className="p-2">
                  {items.map((item: T) => {
                    const id = getId(item);
                    const isSelected = selectedIds.includes(id);
                    return (
                      <div
                        key={id}
                        onClick={() => handleToggleItem(id)}
                        className={`flex cursor-pointer items-center justify-between rounded p-2 transition-colors ${
                          isSelected ? '' : 'text-slate-200 hover:bg-[var(--anl-surface-elev)]'
                        }`}
                        style={
                          isSelected
                            ? { backgroundColor: 'var(--anl-warning)', color: '#ffffff' }
                            : undefined
                        }
                      >
                        <div className="min-w-0 flex-1">
                          <div className="truncate font-medium">{getDisplayName(item)}</div>
                          {getSubtext && (
                            <div
                              className={`truncate text-xs ${isSelected ? '' : 'text-slate-400'}`}
                              style={isSelected ? { color: '#ffffff' } : undefined}
                            >
                              {getSubtext(item)}
                            </div>
                          )}
                        </div>
                        <div
                          className={`ml-2 h-4 w-4 flex-shrink-0 rounded border-2 ${
                            isSelected ? '' : 'border-slate-400'
                          }`}
                          style={
                            isSelected
                              ? { backgroundColor: '#ffffff', borderColor: '#ffffff' }
                              : undefined
                          }
                        >
                          {isSelected && (
                            <div
                              className="h-full w-full rounded-sm"
                              style={{ backgroundColor: 'var(--anl-warning)' }}
                            />
                          )}
                        </div>
                      </div>
                    );
                  })}
                </div>
              )}
            </div>

            {totalPages > 1 && (
              <div className="flex items-center justify-between border-t border-[var(--anl-border)] p-3">
                <button
                  onClick={() => setCurrentPage(currentPage - 1)}
                  disabled={!canPrevPage}
                  className={`flex items-center space-x-1 rounded px-2 py-1 text-xs ${
                    canPrevPage
                      ? 'text-slate-200 hover:bg-[var(--anl-surface-elev)]'
                      : 'cursor-not-allowed text-slate-500'
                  }`}
                >
                  <ChevronLeft size={14} />
                  <span>Previous</span>
                </button>

                <span className="text-xs text-slate-400">
                  Page {currentPage} of {totalPages}
                </span>

                <button
                  onClick={() => setCurrentPage(currentPage + 1)}
                  disabled={!canNextPage}
                  className={`flex items-center space-x-1 rounded px-2 py-1 text-xs ${
                    canNextPage
                      ? 'text-slate-200 hover:bg-[var(--anl-surface-elev)]'
                      : 'cursor-not-allowed text-slate-500'
                  }`}
                >
                  <span>Next</span>
                  <ChevronRight size={14} />
                </button>
              </div>
            )}
          </motion.div>
        )}
      </AnimatePresence>
    </div>
  );
}
